import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Zap, Sparkles, Link as LinkIcon, Wand2, Check, Languages,
  Upload, ShoppingCart, Package, Rocket, Store, Film, RefreshCw,
  Play, Blocks, ShieldCheck, Shield, FileText, EyeOff, FileSignature,
  BadgeCheck, Download, ArrowRight, UnfoldHorizontal, ChevronLeft,
  ChevronRight, ChevronDown, CheckCircle2
} from 'lucide-react';
import { Logo } from '../components/Logo';
import {
  buildAuthModalPath,
  CREDIT_PACK_PRESETS,
  SUBSCRIPTION_PLAN_PRESETS,
  buildCreditsSummary,
  creditsToImages,
} from '../lib/utils';

type ProductTab = 'watch' | 'headphones' | 'sneakers';
type PricingMode = 'monthly' | 'yearly' | 'credits';

export const LandingScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  // Showcase State
  const [activeTab, setActiveTab] = useState<ProductTab>('headphones');

  // Pricing State
  const [pricingMode, setPricingMode] = useState<PricingMode>('credits');

  const creditPackLabels = {
    basic: t('pricing.packBasic'),
    growth: t('pricing.packGrowth'),
    scale: t('pricing.packScale'),
  } as const;

  const subscriptionPlanLabels = {
    starter: t('pricing.planStarter'),
    pro: t('pricing.planPro'),
    business: t('pricing.planBusiness'),
  } as const;

  const creditsLabel = t('pricing.creditsLabel');
  const imagesLabel = t('pricing.imagesLabel');
  const creditsMonthlySuffix = t('pricing.perMonth');

  const buildSubscriptionFeatures = (plan: (typeof SUBSCRIPTION_PLAN_PRESETS)[number]) => {
    const creditsSummary = `${buildCreditsSummary(plan.creditsMonthly ?? 0, creditsLabel, imagesLabel)} ${creditsMonthlySuffix}`;

    switch (plan.id) {
      case 'starter':
        return [
          creditsSummary,
          t('pricing.featBatchStarter'),
          t('pricing.featExportsStarter'),
          t('pricing.featSupportStarter'),
        ];
      case 'pro':
        return [
          creditsSummary,
          t('pricing.featBatchPro'),
          t('pricing.featExportsPro'),
          t('pricing.featSupportPro'),
        ];
      case 'business':
        return [
          creditsSummary,
          t('pricing.featBatchBusiness'),
          t('pricing.featExportsBusiness'),
          t('pricing.featSupportBusiness'),
        ];
      default:
        return [creditsSummary];
    }
  };

  const creditPacks = CREDIT_PACK_PRESETS.map((pack) => ({
    ...pack,
    name: creditPackLabels[pack.id],
    images: creditsToImages(pack.credits),
    featured: pack.highlight,
  }));

  const subscriptionPlans = SUBSCRIPTION_PLAN_PRESETS.map((plan) => ({
    ...plan,
    name: subscriptionPlanLabels[plan.id],
    featured: plan.highlight,
    features: buildSubscriptionFeatures(plan),
  }));

  // FAQ State
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  // X-Ray Slider State
  const [sliderPos, setSliderPos] = useState(50);
  const sliderContainerRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!sliderContainerRef.current) return;
    const rect = sliderContainerRef.current.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const position = ((x - rect.left) / rect.width) * 100;
    setSliderPos(Math.min(Math.max(position, 0), 100));
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const carouselData = {
    watch: {
        img: "https://lh3.googleusercontent.com/aida-public/AB6AXuDfBZh1OojvuN39gXV0reGvy23Do8afS2jLhjVgPdmzNqGqwlb5T6XeYrUxj04bmWyENiRLCWXE9g9C791Z6EQZGGWhrsBkS8oWZRVgm4ith1bnbISV_gito1wSXjdHtYL2IvGnX9lH0yyjgfEr6R8wsGxHDDdt8Y-LUxe3lsZdWphekgPH3dGLgjpI4Au2Gmag-39uJxmLRZw5RrusCUyOg0ggXX9_huLguCOIqk2A2asVX70eU4YLcsdyQNd10A8E8HRuLiHiOXEU",
        title: t('feature1.prodTitle_watch'),
        bullets: [
            { title: t('feature1.bullet1Title_watch'), desc: t('feature1.bullet1Desc_watch') },
            { title: t('feature1.bullet2Title_watch'), desc: t('feature1.bullet2Desc_watch') }
        ],
        keys: [t('feature1.key1_watch'), t('feature1.key2_watch'), t('feature1.key3_watch')],
        hotspots: [
            { top: '35%', left: '45%', label: t('feature1.hotspot1') },
            { bottom: '25%', right: '25%', label: t('feature1.hotspot2') }
        ]
    },
    headphones: {
        img: "https://lh3.googleusercontent.com/aida-public/AB6AXuDWuYitXLVH5zxKsPPZHGNkMdL_GS8zlhlEGsz1CCcIbtnxUeQeKQKI4FQJdb18DQD-2JGk3g42rdswpj15K5pQpD88eZdjZfesGPzlH-iVvPa8TFTaLjkssB8usY9cmbbAMrvQkWCcub4XxpAEQwwpWDfg2XwdWtuw5jt8RhuU_3TCAAeaZyacla3MGaFf-qe6ivjpX8Fcstf1z2Dr1IPsTIMkD8uSFroWaabMN9NbP-a06KdLOulyKz3voU8-UqHhKTQw0MJT-jF5",
        title: t('feature1.prodTitle_headphones'),
        bullets: [
            { title: t('feature1.bullet1Title_headphones'), desc: t('feature1.bullet1Desc_headphones') },
            { title: t('feature1.bullet2Title_headphones'), desc: t('feature1.bullet2Desc_headphones') }
        ],
        keys: [t('feature1.key1_headphones'), t('feature1.key2_headphones'), t('feature1.key3_headphones')],
        hotspots: [
            { top: '30%', right: '35%', label: t('feature1.hotspot_headphones_1') },
            { bottom: '40%', left: '25%', label: t('feature1.hotspot_headphones_2') }
        ]
    },
    sneakers: {
        img: "https://lh3.googleusercontent.com/aida-public/AB6AXuCmtMnqoSSEAZMqXoFIdu0KxaBznAssUXRYN9Ma-o-DURf3marbJwAdJSl36eXFIPppHq1GB5lhz-TNLGRxAVb3-ctBgsOsOlmyl7rmdwrsnNTVq4cu2TLIqaLt2fE0SvGbFQfQwnPHqMytUdeOq3-YTF2HV2tTIeQENmtNkDpZtEqbfWLjfZAmHbYN_2En9s6w0dZuZ4pYjWY2VZR_tgkmpabxiTBlCA-gzpRvlU_Vd8c-CVoqi3bvrYeoZ9VpO6SYEza-mBHaqCH1",
        title: t('feature1.prodTitle_sneakers'),
        bullets: [
            { title: t('feature1.bullet1Title_sneakers'), desc: t('feature1.bullet1Desc_sneakers') },
            { title: t('feature1.bullet2Title_sneakers'), desc: t('feature1.bullet2Desc_sneakers') }
        ],
        keys: [t('feature1.key1_sneakers'), t('feature1.key2_sneakers'), t('feature1.key3_sneakers')],
        hotspots: [
            { top: '45%', left: '40%', label: t('feature1.hotspot_sneakers_1') },
            { bottom: '30%', right: '35%', label: t('feature1.hotspot_sneakers_2') }
        ]
    }
  };

  const openAuthModal = (mode: 'login' | 'signup') => {
    navigate(buildAuthModalPath(location.pathname, location.search, mode));
  };

  const currentProduct = carouselData[activeTab];

  const handlePrev = () => {
      const tabs: ProductTab[] = ['watch', 'headphones', 'sneakers'];
      const index = tabs.indexOf(activeTab);
      setActiveTab(tabs[(index - 1 + tabs.length) % tabs.length]);
  };

  const handleNext = () => {
      const tabs: ProductTab[] = ['watch', 'headphones', 'sneakers'];
      const index = tabs.indexOf(activeTab);
      setActiveTab(tabs[(index + 1) % tabs.length]);
  };

  return (
    <div className="w-full select-none bg-[#F8FAFC]">
      <div className="contained-wrapper">
        <header className="h-24 flex items-center justify-between px-6 md:px-12 border-b border-[#E2E8F0] bg-white sticky top-0 z-[100]">
          <div className="flex items-center gap-3">
             <Logo size="md" />
          </div>
          <nav className="hidden md:flex items-center gap-10">
            <button
              onClick={() => scrollToSection('features')}
              className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-primary transition-colors cursor-pointer"
            >
              {t('nav.features')}
            </button>
            <button
              onClick={() => scrollToSection('pricing')}
              className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-primary transition-colors cursor-pointer"
            >
              {t('nav.pricing')}
            </button>
            <button
              onClick={() => scrollToSection('faq')}
              className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-primary transition-colors cursor-pointer"
            >
              {t('nav.faq')}
            </button>
          </nav>
          <div className="flex items-center gap-4 mr-16 md:mr-0">
            <button
                onClick={() => openAuthModal('login')}
                className="px-6 py-2 text-xs font-bold uppercase tracking-widest text-slate-600 hover:text-primary transition-all"
            >
                {t('nav.login')}
            </button>
            <button
                onClick={() => openAuthModal('signup')}
                className="bg-primary hover:bg-primary/90 text-white px-7 py-3 rounded-full text-xs font-extrabold uppercase tracking-widest transition-all shadow-lg shadow-primary/10 hidden md:block active:scale-95 hover:shadow-primary/20"
            >
                {t('nav.startFree')}
            </button>
          </div>
        </header>

        <main className="bg-[#F8FAFC]">
          {/* Hero Section */}
          <section className="content-section flex flex-col items-center text-center pt-24 pb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-50 text-slate-500 border border-slate-200 text-[10px] font-bold uppercase tracking-[0.2em] mb-10 animate-in fade-in slide-in-from-top-4 duration-1000">
              <Sparkles size={14} className="text-primary" />
              {t('hero.badge')}
            </div>
            <h1 className="text-5xl md:text-6xl font-black leading-[1.15] tracking-tight mb-8 max-w-4xl text-slate-900 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-150">
              {t('hero.title')} <br /><span className="text-primary/90">{t('hero.titleHighlight')}</span>
            </h1>
            <p className="text-lg text-slate-500 max-w-2xl mb-16 leading-loose animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-300">
              {t('hero.subtitle')}
            </p>
            <div className="w-full max-w-2xl mx-auto mb-20 animate-in fade-in slide-in-from-bottom-6 duration-1000 delay-500">
              <div className="bg-white p-2 rounded-2xl subtle-border shadow-xl shadow-slate-100 flex flex-col md:flex-row gap-2">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <LinkIcon size={20} className="text-slate-300" />
                  </div>
                  <input className="block w-full pl-12 pr-4 py-5 bg-transparent border-none focus:ring-0 text-slate-900 placeholder:text-slate-300 font-medium text-sm outline-none" placeholder={t('hero.placeholder')} type="text" />
                </div>
                <button
                  onClick={() => navigate('/import')}
                  className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 whitespace-nowrap shadow-md shadow-primary/20 active:scale-95 hover:shadow-lg hover:shadow-primary/30"
                >
                  <Wand2 size={18} />
                  {t('hero.button')}
                </button>
              </div>
            </div>
          </section>

          {/* Feature 1: Showcase Carousel */}
          <section id="features" className="content-section bg-transparent">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-black tracking-tight mb-4 text-[#0F172A]">{t('feature1.title')}</h2>
              <p className="text-slate-500 font-medium text-base">{t('feature1.subtitle')}</p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 mb-14">
              {[
                  { id: 'watch', label: t('feature1.tab1') },
                  { id: 'headphones', label: t('feature1.tab2') },
                  { id: 'sneakers', label: t('feature1.tab3') }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as ProductTab)}
                  className={`px-8 py-3 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all ${
                    activeTab === tab.id
                    ? 'bg-slate-900 text-white shadow-lg shadow-slate-200 ring-4 ring-slate-100'
                    : 'bg-white text-slate-400 border border-[#E2E8F0] hover:text-slate-600'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="relative max-w-6xl mx-auto">
                {/* Carousel Nav Arrows */}
                <button onClick={handlePrev} className="absolute -left-4 md:-left-16 top-1/2 -translate-y-1/2 p-2 text-slate-300 hover:text-primary transition-colors hidden xl:block z-20">
                    <ChevronLeft size={64} strokeWidth={1} />
                </button>
                <button onClick={handleNext} className="absolute -right-4 md:-right-16 top-1/2 -translate-y-1/2 p-2 text-slate-300 hover:text-primary transition-colors hidden xl:block z-20">
                    <ChevronRight size={64} strokeWidth={1} />
                </button>

                <div className="bg-white rounded-[2rem] border border-[#E2E8F0] shadow-[0_20px_50px_rgba(0,0,0,0.04)] overflow-hidden grid grid-cols-12 animate-in fade-in duration-700">
                    <div className="col-span-12 lg:col-span-7 border-r border-[#E2E8F0] p-10 flex flex-col gap-8">
                        <div className="flex items-center gap-8 border-b border-slate-100 pb-px overflow-x-auto no-scrollbar">
                            <button className="text-[10px] font-black uppercase tracking-widest text-primary border-b-2 border-primary pb-4 -mb-[2px] transition-colors whitespace-nowrap">{t('feature1.imgBtn1')}</button>
                            <button className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 pb-4 transition-colors whitespace-nowrap">{t('feature1.imgBtn2')}</button>
                            <button className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 pb-4 transition-colors whitespace-nowrap">{t('feature1.imgBtn3')}</button>
                        </div>
                        <div className="relative group">
                            <div className="aspect-[4/3] rounded-2xl bg-white overflow-hidden relative shadow-sm border border-slate-100">
                                <img key={activeTab} alt="Product Showcase" className="w-full h-full object-cover scale-105 animate-in fade-in zoom-in-95 duration-1000" src={currentProduct.img} />

                                {currentProduct.hotspots.map((h, i) => (
                                    <div key={i} className="absolute group/hotspot cursor-help" style={{ top: h.top, bottom: h.bottom, left: h.left, right: h.right }}>
                                        <div className="pulsing-dot"><span></span><span></span></div>
                                        <div className="hotspot-label">{h.label}</div>
                                    </div>
                                ))}

                                <div className="absolute top-6 right-6">
                                    <span className="bg-white/95 backdrop-blur-md border border-slate-200/50 px-4 py-2 rounded-full text-[10px] font-bold text-primary flex items-center gap-2 uppercase tracking-widest shadow-sm">
                                        <Sparkles size={14} /> {t('feature1.aiBadge')}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('feature1.marketingTitle')}</p>
                                <span className="text-[10px] font-bold text-primary uppercase tracking-widest cursor-pointer hover:underline">{t('feature1.viewAll')}</span>
                            </div>
                            <div className="flex gap-4 overflow-x-auto pb-2 no-scrollbar">
                                <div className="shrink-0 w-32 aspect-square rounded-xl bg-slate-50 border-2 border-primary ring-4 ring-primary/5 overflow-hidden cursor-pointer shadow-sm">
                                    <img className="w-full h-full object-cover" src={currentProduct.img} />
                                </div>
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="shrink-0 w-32 aspect-square rounded-xl bg-slate-50 border border-slate-100 overflow-hidden group/thumb cursor-pointer hover:border-slate-300 transition-all opacity-60 hover:opacity-100">
                                        <img className="w-full h-full object-cover transition-all" src={i === 1 ? "https://lh3.googleusercontent.com/aida-public/AB6AXuDWuYitXLVH5zxKsPPZHGNkMdL_GS8zlhlEGsz1CCcIbtnxUeQeKQKI4FQJdb18DQD-2JGk3g42rdswpj15K5pQpD88eZdjZfesGPzlH-iVvPa8TFTaLjkssB8usY9cmbbAMrvQkWCcub4XxpAEQwwpWDfg2XwdWtuw5jt8RhuU_3TCAAeaZyacla3MGaFf-qe6ivjpX8Fcstf1z2Dr1IPsTIMkD8uSFroWaabMN9NbP-a06KdLOulyKz3voU8-UqHhKTQw0MJT-jF5" : i === 2 ? "https://lh3.googleusercontent.com/aida-public/AB6AXuDWuYitXLVH5zxKsPPZHGNkMdL_GS8zlhlEGsz1CCcIbtnxUeQeKQKI4FQJdb18DQD-2JGk3g42rdswpj15K5pQpD88eZdjZfesGPzlH-iVvPa8TFTaLjkssB8usY9cmbbAMrvQkWCcub4XxpAEQwwpWDfg2XwdWtuw5jt8RhuU_3TCAAeaZyacla3MGaFf-qe6ivjpX8Fcstf1z2Dr1IPsTIMkD8uSFroWaabMN9NbP-a06KdLOulyKz3voU8-UqHhKTQw0MJT-jF5" : "https://lh3.googleusercontent.com/aida-public/AB6AXuCmtMnqoSSEAZMqXoFIdu0KxaBznAssUXRYN9Ma-o-DURf3marbJwAdJSl36eXFIPppHq1GB5lhz-TNLGRxAVb3-ctBgsOsOlmyl7rmdwrsnNTVq4cu2TLIqaLt2fE0SvGbFQfQwnPHqMytUdeOq3-YTF2HV2tTIeQENmtNkDpZtEqbfWLjfZAmHbYN_2En9s6w0dZuZ4pYjWY2VZR_tgkmpabxiTBlCA-gzpRvlU_Vd8c-CVoqi3bvrYeoZ9VpO6SYEza-mBHaqCH1"} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="col-span-12 lg:col-span-5 bg-[#F8FAFC] p-10 flex flex-col h-full border-t lg:border-t-0 lg:border-l border-[#E2E8F0]">
                        <div className="flex flex-col gap-8 flex-1">
                            <div key={activeTab} className="animate-in fade-in slide-in-from-left-4 duration-500">
                                <div className="flex items-center gap-2 mb-4">
                                    <span className="bg-primary/10 text-primary text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-[0.1em]">{t('feature1.listingTitleTag')}</span>
                                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-auto flex items-center gap-1">
                                        <Languages size={12} /> {t('feature1.translated')}
                                    </span>
                                </div>
                                <h3 className="text-2xl font-black text-[#0F172A] leading-tight">{currentProduct.title}</h3>
                            </div>
                            <div className="space-y-6">
                                <div>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">{t('feature1.bulletTitle')}</p>
                                    <ul className="space-y-4">
                                        {currentProduct.bullets.map((b, i) => (
                                            <li key={i} className="flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2 duration-500" style={{ animationDelay: `${i * 100}ms` }}>
                                                <div className="size-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                                                    <Check size={14} className="text-primary font-black" />
                                                </div>
                                                <p className="text-sm text-slate-600 font-medium leading-relaxed"><strong className="text-slate-900">{b.title}</strong> {b.desc}</p>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">{t('feature1.seoTitle')}</p>
                                    <div className="flex flex-wrap gap-2">
                                        {currentProduct.keys.map((k, i) => (
                                            <span key={i} className="bg-white px-3 py-1.5 rounded-full text-[10px] font-bold text-slate-600 border border-slate-200 animate-in zoom-in duration-300" style={{ animationDelay: `${i * 100}ms` }}>{k}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-12 pt-8 border-t border-slate-200/60 relative group">
                            <button
                                onClick={() => alert('Demo: Product pushed to marketplaces successfully!')}
                                className="w-full bg-[#0F172A] text-white py-5 rounded-xl text-xs font-bold uppercase tracking-[0.15em] hover:bg-slate-800 transition-all flex items-center justify-center gap-3 shadow-xl shadow-slate-200 relative z-10 active:scale-95"
                            >
                                <Upload size={18} />
                                {t('feature1.pushBtn')}
                            </button>
                            <div className="mt-8">
                                <div className="logo-marquee-container w-full overflow-hidden mask-fade relative h-12">
                                    <div className="flex whitespace-nowrap animate-scroll items-center gap-12 w-fit">
                                        {[1, 2].map((iter) => (
                                            <div key={iter} className="flex items-center gap-12">
                                                <div className="logo-grayscale flex items-center gap-2"><ShoppingCart size={14} /><span className="text-[10px] font-bold uppercase tracking-wider">Shopify</span></div>
                                                <div className="logo-grayscale flex items-center gap-2"><Package size={14} /><span className="text-[10px] font-bold uppercase tracking-wider">Amazon</span></div>
                                                <div className="logo-ozon-active flex items-center gap-2 group/ozon relative cursor-default py-2">
                                                    <Rocket size={14} className="text-ozon-blue" />
                                                    <span className="text-[10px] font-black uppercase tracking-wider bg-gradient-to-r from-ozon-blue to-ozon-pink bg-clip-text text-transparent">Ozon</span>
                                                    <div className="flex items-center gap-1.5 pl-1">
                                                        <div className="sync-indicator-dot"></div>
                                                        <span className="text-[8px] font-bold text-emerald-600 uppercase tracking-tighter">Full Sync</span>
                                                    </div>
                                                </div>
                                                <div className="logo-grayscale flex items-center gap-2"><Store size={14} /><span className="text-[10px] font-bold uppercase tracking-wider">eBay</span></div>
                                                <div className="logo-grayscale flex items-center gap-2"><Film size={14} /><span className="text-[10px] font-bold uppercase tracking-wider">TikTok Shop</span></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-center gap-2 mt-4">
                                    <RefreshCw size={12} />
                                    {t('feature1.syncText')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Carousel Dots */}
                <div className="flex justify-center items-center gap-3 mt-10">
                    {['watch', 'headphones', 'sneakers'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab as ProductTab)}
                            className={`transition-all duration-300 ${
                                activeTab === tab
                                ? 'w-2.5 h-2.5 rounded-full bg-primary shadow-sm'
                                : 'w-2 h-2 rounded-full bg-slate-200 hover:bg-slate-300'
                            }`}
                        />
                    ))}
                </div>
            </div>
          </section>

          {/* Feature 2: Core Platform */}
          <section className="content-section">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-black tracking-tight mb-4 text-slate-900 uppercase tracking-widest">{t('feature2.title')}</h2>
              <p className="text-slate-400 font-medium">{t('feature2.subtitle')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto">
              <div className="flex flex-col group">
                <div className="w-full aspect-video bg-slate-50 rounded-2xl relative overflow-hidden mb-8 subtle-border cursor-pointer">
                  <img className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" src="https://lh3.googleusercontent.com/aida-public/AB6AXuB-tl2TfyAPsbhf3tSz54tleQBwNY2Fh1wne6XVxdhr-87EO7AnOOxnw7VlLyJXmOr07VKTpoiskcMsBqRuFk24BB1Gz5E_oIeQWJ7SY7rrZU4eq98ezZPpAC7VWbndqiK7JtL7CtOIn1DUTtUIy3EFvSYwmqzTIYcwdUCwEdtMRz-s82a6rvYfjtD60PlXb7f3SjdjOWM7LqZholm3Fw_0SiDNo1l9-S-xhyqnH7Y3bB01AUk08riNUF3IxID7FoYLuIWdD7Dhwism" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="size-16 bg-white rounded-full flex items-center justify-center pl-1 group-hover:scale-110 transition-transform duration-300 shadow-xl border border-slate-100">
                      <Play size={36} className="text-primary fill-primary" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="size-10 rounded-lg bg-blue-50 text-primary flex items-center justify-center border border-blue-100">
                    <Sparkles size={20} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 tracking-tight">{t('feature2.card1Title')}</h3>
                </div>
                <p className="text-slate-500 mb-8 leading-loose text-sm">{t('feature2.card1Desc')}</p>
              </div>
              <div className="flex flex-col group">
                <div className="w-full aspect-video bg-slate-50 rounded-2xl relative overflow-hidden mb-8 subtle-border cursor-pointer">
                  <img className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" src="https://lh3.googleusercontent.com/aida-public/AB6AXuDwKEeOZw9F7j9mQxErT9Aml0TVHSm7ujUGcABdmP4bGYXrY8QZcoPIOcvEpPUYLmSsZ8N-dLGpsmjHimNp_iYQVeFurr6PPoI7WjHvIjOtTeaEzb-w3tTZfPthJv_1NX501UpD5sh5CcY8VcI79dcC7v6sNwAs0Nt-B7lmr24pCJb3m1ot41jHKL2UsEO0HSsjCCxi1yfAUbT-r075-T0egeZPs8ZQiHHYyhtdoy7Vp-KAoTn0zqd9jkBTXRJH1SPHBF5xA2LOAj6B" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="size-16 bg-white rounded-full flex items-center justify-center pl-1 group-hover:scale-110 transition-transform duration-300 shadow-xl border border-slate-100">
                        <Play size={36} className="text-primary fill-primary" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="size-10 rounded-lg bg-slate-50 text-slate-900 flex items-center justify-center border border-slate-200">
                    <Blocks size={20} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 tracking-tight">{t('feature2.card2Title')}</h3>
                </div>
                <p className="text-slate-500 mb-8 leading-loose text-sm">{t('feature2.card2Desc')}</p>
              </div>
            </div>
          </section>

          {/* Pricing Section */}
          <section id="pricing" className="content-section bg-transparent">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4 text-slate-900">{t('pricing.title')}</h2>
              <p className="text-slate-500 font-medium max-w-xl mx-auto">{t('pricing.subtitle')}</p>
            </div>

            <div className="flex justify-center mb-20">
                <div className="bg-white p-2 rounded-full flex items-center border border-[#E2E8F0] shadow-sm">
                    <div className="flex items-center bg-[#F1F5F9] p-1 rounded-full">
                        <button
                            onClick={() => setPricingMode('monthly')}
                            className={`px-7 py-3 text-[10px] font-black uppercase tracking-[0.15em] transition-all duration-300 rounded-full ${pricingMode === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            {t('pricing.monthly')}
                        </button>
                        <button
                            onClick={() => setPricingMode('yearly')}
                            className={`px-7 py-3 text-[10px] font-black uppercase tracking-[0.15em] transition-all duration-300 rounded-full flex items-center gap-2 ${pricingMode === 'yearly' ? 'bg-primary text-white shadow-lg shadow-blue-500/20' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            {t('pricing.yearly')}
                            <span className={`px-2 py-0.5 rounded-[4px] text-[7px] font-black tracking-normal ${pricingMode === 'yearly' ? 'bg-white text-primary' : 'bg-emerald-500 text-white'}`}>{t('pricing.save')}</span>
                        </button>
                    </div>
                    <div className="w-px h-8 bg-[#E2E8F0] mx-4 md:mx-6"></div>
                    <div className="relative group">
                        <div className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap">
                            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">{t('pricing.bestForGuests')}</span>
                        </div>
                        <button
                            onClick={() => setPricingMode('credits')}
                            className={`px-8 py-2 rounded-full transition-all duration-300 flex flex-col items-center ${pricingMode === 'credits' ? 'bg-primary text-white shadow-lg shadow-blue-500/20' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            <span className={`text-[7px] font-bold mb-1 ${pricingMode === 'credits' ? 'text-white/70' : 'text-slate-400'}`}>{t('pricing.onDemand')}</span>
                            <div className="flex items-center gap-1.5">
                                <Zap size={14} className={pricingMode === 'credits' ? 'fill-white' : ''} />
                                <span className="text-[10px] font-black uppercase tracking-[0.15em]">{t('pricing.credits')}</span>
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 max-w-6xl mx-auto min-h-[500px]">
                {pricingMode === 'credits' ? (
                   <>
                    {creditPacks.map((pack, i) => (
                        <div key={i} className={`bg-white p-10 rounded-[2rem] border transition-all duration-500 flex flex-col group animate-in fade-in zoom-in-95 ${pack.featured ? 'border-2 border-primary shadow-[0_20px_50px_rgba(37,99,235,0.1)] scale-105 z-10 relative' : 'border-[#E2E8F0] shadow-sm hover:border-slate-300'}`}>
                            {pack.featured && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] font-black px-5 py-1.5 rounded-full uppercase tracking-[0.15em] shadow-lg shadow-primary/30">{t('pricing.bestValue')}</div>}
                            <div className="mb-10">
                                <h3 className={`text-xs font-black uppercase tracking-widest mb-6 ${pack.featured ? 'text-primary' : 'text-slate-400'}`}>{pack.name}</h3>
                                <div className="flex items-baseline gap-1">
                                    <span className="text-5xl font-black text-slate-900">${pack.priceUsd}</span>
                                </div>
                                <p className="text-[10px] font-bold text-slate-400 mt-4 uppercase tracking-widest">{pack.credits} {t('pricing.creditsLabel')} · {pack.images} {t('pricing.imagesLabel')}</p>
                            </div>
                            <ul className="space-y-5 mb-12 flex-1">
                                {[t('pricing.featExpires'), t('pricing.featAi'), t('pricing.featPsd'), t('pricing.featPriority')].map((feat, j) => (
                                    <li key={j} className="flex items-center gap-3">
                                        <CheckCircle2 size={18} className="text-emerald-500" />
                                        <span className={`text-sm ${j === 0 && pack.featured ? 'text-slate-900 font-bold' : 'text-slate-600 font-medium'}`}>{feat}</span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={() => openAuthModal('signup')}
                                className={`w-full py-4 rounded-xl text-xs font-black uppercase tracking-widest transition-all active:scale-95 ${pack.featured ? 'bg-primary text-white hover:bg-primary/90 shadow-xl shadow-primary/20' : 'border border-[#E2E8F0] text-slate-900 hover:bg-slate-50'}`}
                            >
                                {t('pricing.purchase')}
                            </button>
                        </div>
                    ))}
                   </>
                ) : (
                    <>
                    {subscriptionPlans.map((plan, i) => {
                        const price = pricingMode === 'monthly' ? plan.monthlyUsd : plan.yearlyUsd;
                        const period = pricingMode === 'monthly' ? t('pricing.perMonth') : t('pricing.perYear');

                        return (
                        <div key={i} className={`bg-white p-10 rounded-[2rem] border transition-all duration-500 flex flex-col group animate-in fade-in zoom-in-95 ${plan.featured ? 'border-2 border-primary shadow-[0_20px_50px_rgba(37,99,235,0.1)] scale-105 z-10 relative' : 'border-[#E2E8F0] shadow-sm hover:border-slate-300'}`}>
                            {plan.featured && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] font-black px-5 py-1.5 rounded-full uppercase tracking-[0.15em] shadow-lg shadow-primary/30">{t('pricing.bestValue')}</div>}
                            <div className="mb-10">
                                <h3 className={`text-xs font-black uppercase tracking-widest mb-6 ${plan.featured ? 'text-primary' : 'text-slate-400'}`}>{plan.name}</h3>
                                <div className="flex items-baseline gap-1">
                                    <span className="text-5xl font-black text-slate-900">${price}</span>
                                    <span className="text-slate-400 font-bold">{period}</span>
                                </div>
                            </div>
                            <ul className="space-y-5 mb-12 flex-1">
                                {plan.features.map((feat, j) => (
                                    <li key={j} className="flex items-center gap-3">
                                        <CheckCircle2 size={18} className="text-emerald-500" />
                                        <span className={`text-sm ${plan.featured ? 'text-slate-900 font-bold' : 'text-slate-600 font-medium'}`}>{feat}</span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={() => openAuthModal('signup')}
                                className={`w-full py-4 rounded-xl text-xs font-black uppercase tracking-widest transition-all active:scale-95 ${plan.featured ? 'bg-primary text-white hover:bg-primary/90 shadow-xl shadow-primary/20 hover:shadow-primary/30' : 'border border-[#E2E8F0] text-slate-900 hover:bg-slate-50 hover:border-slate-300'}`}
                            >
                                {t('nav.startFree')}
                            </button>
                        </div>
                    )})}
                    </>
                )}
            </div>
          </section>

          {/* Feature 3: Legal & Security Slider */}
          <section data-feature3-section="true" className="content-section bg-transparent pt-12">
            <div className="text-center mb-16 relative z-10">
              <div
                data-feature3-badge="true"
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-primary border border-blue-100 text-[10px] font-bold uppercase tracking-widest mb-6 relative z-10"
              >
                <ShieldCheck size={14} />
                {t('feature3.badge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4 text-[#0F172A]">{t('feature3.title')}</h2>
              <p className="text-slate-500 font-medium max-w-2xl mx-auto">{t('feature3.subtitle')}</p>
            </div>

            <div
              ref={sliderContainerRef}
              onMouseMove={handleMouseMove}
              onTouchMove={handleMouseMove}
              className="max-w-6xl mx-auto mb-20 relative rounded-[2rem] overflow-hidden border border-slate-200 shadow-2xl bg-white cursor-ew-resize group"
            >
              <div className="flex h-[500px] relative overflow-hidden">
                {/* Normal View */}
                <div className="w-full h-full absolute inset-0">
                  <img alt="Normal View" className="w-full h-full object-cover" src="https://lh3.googleusercontent.com/aida-public/AB6AXuDfBZh1OojvuN39gXV0reGvy23Do8afS2jLhjVgPdmzNqGqwlb5T6XeYrUxj04bmWyENiRLCWXE9g9C791Z6EQZGGWhrsBkS8oWZRVgm4ith1bnbISV_gito1wSXjdHtYL2IvGnX9lH0yyjgfEr6R8wsGxHDDdt8Y-LUxe3lsZdWphekgPH3dGLgjpI4Au2Gmag-39uJxmLRZw5RrusCUyOg0ggXX9_huLguCOIqk2A2asVX70eU4YLcsdyQNd10A8E8HRuLiHiOXEU" />
                  <div className="absolute bottom-12 left-12 opacity-40 mix-blend-overlay">
                    <div className="flex items-center gap-2 text-white">
                      <Zap size={20} />
                      <span className="text-sm font-black uppercase tracking-tighter">{t('feature3.verified')}</span>
                    </div>
                  </div>
                  <div className="absolute top-8 left-8 bg-white/90 backdrop-blur px-4 py-2 rounded-lg border border-slate-200 shadow-sm">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-900">{t('feature3.watermark')}</span>
                  </div>
                </div>

                {/* Forensic View (Clipped) */}
                <div
                  className="absolute inset-0 bg-slate-900/90 backdrop-blur-sm z-10 transition-none border-r-2 border-primary/40"
                  style={{ width: `${sliderPos}%` }}
                >
                  <div className="w-[1000px] h-full relative">
                    <div className="absolute inset-0 opacity-20 pointer-events-none" style={{backgroundImage: 'radial-gradient(#2563EB 1px, transparent 1px)', backgroundSize: '20px 20px'}}></div>
                    <div className="p-12 text-blue-400 font-mono text-[10px] leading-relaxed uppercase">
                      <p className="mb-2 text-blue-100 font-bold">{t('trust.forensicData')}</p>
                      <p>0x45A2-FID: OCS-PRD-88219</p>
                      <p>TS: 2024-11-20T14:32:01.000Z</p>
                      <p>LAT: 55.7558° N, 37.6173° E</p>
                      <p>OWNER_HASH: e3b0c44298fc1c149afbf</p>
                      <p className="mt-4 text-emerald-400 flex items-center gap-2">
                        <Shield size={12} />
                        {t('feature3.hash')}
                      </p>
                    </div>
                    <div className="absolute bottom-12 right-12 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="bg-primary text-white p-4 rounded-2xl shadow-2xl flex flex-col items-center gap-2 border border-blue-400/50 scale-75 md:scale-100">
                            <FileText size={32} />
                            <span className="text-[9px] font-black uppercase tracking-widest">{t('feature3.cert')}</span>
                        </div>
                    </div>
                  </div>
                </div>

                {/* Handle */}
                <div
                  className="absolute top-0 bottom-0 z-20 pointer-events-none"
                  style={{ left: `${sliderPos}%` }}
                >
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-2xl flex items-center justify-center border border-slate-200">
                    <UnfoldHorizontal size={16} className="text-slate-400" />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
              {[
                  { icon: EyeOff, title: t('feature3.card1Title'), desc: t('feature3.card1Desc'), color: 'text-primary', bg: 'bg-blue-50' },
                  { icon: FileSignature, title: t('feature3.card2Title'), desc: t('feature3.card2Desc'), color: 'text-slate-900', bg: 'bg-slate-50' },
                  { icon: BadgeCheck, title: t('feature3.card3Title'), desc: t('feature3.card3Desc'), color: 'text-emerald-600', bg: 'bg-emerald-50' }
              ].map((card, i) => (
                <div key={i} className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow group">
                    <div className={`size-12 ${card.bg} rounded-xl flex items-center justify-center ${card.color} mb-6 transition-transform group-hover:scale-110`}>
                        <card.icon size={24} />
                    </div>
                    <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-3">{card.title}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed font-medium">{card.desc}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col items-center gap-10">
              <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 w-full">
                <div className="flex flex-col items-center md:items-start gap-3">
                  <button
                    onClick={() => alert('Downloading Security Whitepaper...')}
                    className="bg-white border border-[#E2E8F0] hover:border-slate-300 px-8 py-4 rounded-xl flex items-center gap-3 transition-all shadow-sm group active:scale-95"
                  >
                    <Download size={18} className="text-slate-400 group-hover:text-primary transition-colors" />
                    <span className="text-xs font-black text-slate-900 uppercase tracking-widest">{t('feature3.downloadBtn')}</span>
                  </button>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('feature3.downloadSub')}</p>
                </div>
                <div className="h-px w-12 bg-slate-200 hidden md:block"></div>
                <button
                  onClick={() => navigate('/trust')}
                  className="inline-flex items-center gap-2 text-xs font-black text-primary uppercase tracking-[0.2em] hover:gap-3 transition-all py-4 cursor-pointer"
                >
                  {t('feature3.learnMore')}
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section id="faq" className="content-section bg-transparent">
              <div className="max-w-6xl mx-auto">
                  <div className="text-center mb-16">
                      <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4 text-[#0F172A]">{t('faq.title')}</h2>
                      <p className="text-slate-500 font-medium max-w-lg mx-auto">{t('faq.subtitle')}</p>
                  </div>
                  <div className="space-y-4">
                      {[1, 2, 3, 4].map((i) => (
                          <div
                              key={i}
                              className={`group border rounded-2xl overflow-hidden transition-all duration-300 ${openFaq === i-1 ? 'bg-white border-primary shadow-lg shadow-blue-500/5' : 'bg-white border-[#E2E8F0] hover:border-slate-300'}`}
                          >
                              <button
                                  onClick={() => setOpenFaq(openFaq === i-1 ? null : i-1)}
                                  className="w-full flex items-center justify-between p-7 cursor-pointer text-left focus:outline-none"
                              >
                                  <span className={`text-sm font-bold uppercase tracking-wider transition-colors ${openFaq === i-1 ? 'text-primary' : 'text-slate-900'}`}>{t(`faq.q${i}` as any)}</span>
                                  <ChevronDown size={20} className={`text-slate-400 transition-transform duration-300 ${openFaq === i-1 ? 'rotate-180 text-primary' : ''}`} />
                              </button>
                              <div className={`transition-all duration-300 ease-in-out ${openFaq === i-1 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}>
                                  <div className="px-7 pb-7">
                                      <p className="text-sm text-slate-600 leading-relaxed font-medium">
                                          {t(`faq.a${i}` as any)}
                                      </p>
                                  </div>
                              </div>
                          </div>
                      ))}
                  </div>
                  <div className="text-center mt-12">
                      <a className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] hover:text-primary transition-colors cursor-pointer">
                          {t('faq.contact')}
                      </a>
                  </div>
              </div>
          </section>

          {/* CTA Footer */}
          <section className="px-6 md:px-12 pb-24 bg-transparent pt-12">
            <div className="bg-slate-900 rounded-[2.5rem] p-16 md:p-24 text-center text-white relative overflow-hidden group">
              <div className="relative z-10">
                <h2 className="text-4xl md:text-5xl font-black mb-8 leading-tight tracking-tight transition-transform group-hover:scale-105 duration-700">{t('cta.title')} <br />{t('cta.titleBreak')}</h2>
                <p className="text-slate-400 text-lg mb-12 max-w-xl mx-auto font-medium">{t('cta.subtitle')}</p>
                <button
                    onClick={() => openAuthModal('signup')}
                    className="bg-primary text-white px-10 py-5 rounded-full text-xs font-black uppercase tracking-[0.2em] hover:opacity-90 transition-all shadow-2xl shadow-primary/20 active:scale-95"
                >
                  {t('cta.button')}
                </button>
              </div>
              <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full -mr-32 -mt-32 blur-3xl group-hover:bg-primary/10 transition-colors duration-1000"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full -ml-32 -mb-32 blur-3xl group-hover:bg-white/10 transition-colors duration-1000"></div>
            </div>
          </section>
        </main>

        <footer className="py-12 px-6 md:px-12 bg-transparent">
          <div className="flex flex-col md:flex-row justify-between items-center gap-10">
            <div className="flex items-center gap-2">
              <Logo size="sm" />
            </div>
            <nav className="flex gap-10 text-[10px] font-bold uppercase tracking-widest text-slate-400">
              <a className="hover:text-primary transition-colors cursor-pointer">{t('footer.privacy')}</a>
              <a className="hover:text-primary transition-colors cursor-pointer">{t('footer.terms')}</a>
              <a className="hover:text-primary transition-colors cursor-pointer">{t('footer.support')}</a>
            </nav>
            <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">{t('footer.rights')}</p>
          </div>
        </footer>
      </div>
    </div>
  );
};
